import { useEffect } from 'react';
import { makeStyles } from '@material-ui/styles';
import { ArrowLeftSLine, MainButton, Theme } from '@bizzabo/design-system';
import cn from 'clsx';

import useTypeTracking from '../../../../../analytics';
import { CSS_TRANSITION_DELAY, DrawerNav } from '../const';
import { useDrawerContentAtom } from '../../atoms';
import { BackButton } from '../../Components';
import {
    SchedulingPreferencesFormFields,
    useSchedulingPreferencesForm,
    withDefaults,
    formatAvailability,
    SchedulingPreferencesFormValues,
    useUpdateSchedulingPreferences,
} from 'people/UserSettings/SchedulingPreferencesForm';
import useSchedulingPreferences from 'people/hooks/useSchedulingPreferences';
import SaveButton from 'people/components/SaveButton';

import { instanaReportError } from '@bizzabo/instana-api';
import { SubmitHandler } from 'react-hook-form';
import MeetingSettingsModalThemeProvider from '../../../../../customizationTheme/MeetingSettingsModalThemeProvider';
import { useSetAtom } from 'jotai';
import currentTimeZoneIdAtom, {
    setCurrentTimeZoneId as setTimeZoneId,
} from '../../../../../meetings/schedule/timeZoneId';
import { Availability } from '../../../../../people/UserSettings/SchedulingPreferencesForm/useSchedulingPreferencesForm/types';
import { validateEmptyInterval } from '../../../../../people/UserSettings/SchedulingPreferencesForm/helpers';

export interface DrawerMeetingsProps {
    eventId: number;
}

export const DrawerMeetings: React.FC<DrawerMeetingsProps> = ({ eventId }) => {
    const { trackClickEvent } = useTypeTracking({ data: { category: 'MEETINGS_SETTINGS' } });

    const updateSchedulingPreferences = useUpdateSchedulingPreferences();

    const {
        isLoading: isLoadingData,
        data: schedulingPreferencesData,
        isError,
    } = useSchedulingPreferences(eventId);

    const setCurrentTimeZoneId = useSetAtom(currentTimeZoneIdAtom);

    const form = useSchedulingPreferencesForm(withDefaults({}));
    const { handleSubmit, formState, reset, clearErrors, setError } = form;
    const formHasErrors = Object.keys(formState.errors).length > 0;

    const formEmptyValidation = (availability: Availability['availability']) => {
        clearErrors('availability');
        const errors = validateEmptyInterval(availability);
        errors.forEach(({ field, message }) => {
            setError(field, { message });
        });
        return errors;
    };

    const onSubmit: SubmitHandler<SchedulingPreferencesFormValues> = formValues => {
        const errors = formEmptyValidation(formValues.availability);
        if (errors.length) {
            return;
        }

        setCurrentTimeZoneId(formValues.timezoneId);
        setTimeZoneId(formValues.timezoneId);
        trackClickEvent({ subCategory: 'SAVE_MANAGEMENT_SETTINGS' });
        try {
            updateSchedulingPreferences({
                eventId: eventId,
                payload: {
                    timezoneId: formValues.timezoneId,
                    meetingDuration: formValues.meetingDuration,
                    schedulingHours: formatAvailability(
                        formValues.availability,
                        formValues.timezoneId
                    ),
                    bufferTime: formValues.bufferTime,
                    leadTime: formValues.leadTime,
                    maxMeetingsPerDay: formValues.maxMeetingsPerDay,
                },
                schedulingPreferencesData,
            });
        } catch (error) {
            instanaReportError({ error });
        }
    };

    const { setDrawerContent } = useDrawerContentAtom();
    const handleCancelClick = () => {
        setDrawerContent(DrawerNav.MY_SETTINGS_CONTENT);
    };

    useEffect(() => {
        reset(withDefaults(schedulingPreferencesData));
    }, [schedulingPreferencesData, reset]);

    const classes = useStyles();

    if (isLoadingData || isError) {
        return null;
    }

    return (
        <MeetingSettingsModalThemeProvider>
            <nav className={classes.meetings}>
                <div className={classes.backButton}>
                    <BackButton to={DrawerNav.MY_SETTINGS_CONTENT} buttonText="">
                        <ArrowLeftSLine />
                    </BackButton>
                </div>
                <form onSubmit={handleSubmit(onSubmit)} className={classes.drawerMeetingsForm}>
                    <SchedulingPreferencesFormFields
                        form={form}
                        schedulingHoursBase={schedulingPreferencesData.schedulingHoursBase}
                    />
                    <div className={classes.saveFooter}>
                        <MainButton
                            className={cn(classes.cancelButton, classes.formButton)}
                            onClick={handleCancelClick}
                            disabled={formState.isSubmitting}
                        >
                            Cancel
                        </MainButton>
                        <SaveButton
                            className={cn(classes.saveButton, classes.formButton)}
                            disabled={!formState.isDirty || formHasErrors}
                            isLoading={formState.isSubmitting}
                            isSubmitSuccesful={formState.isSubmitSuccessful}
                            type="submit"
                            variant="fill"
                        >
                            Save
                        </SaveButton>
                    </div>
                </form>
            </nav>
        </MeetingSettingsModalThemeProvider>
    );
};

export const useStyles = makeStyles<Theme>(
    theme => ({
        meetings: {
            width: '100%',
            height: '100%',
            transition: 'all 300ms ease-in',
            animation: `$appear ${CSS_TRANSITION_DELAY}ms ease`,
        },
        '@keyframes appear': {
            '0%': {
                opacity: 0,
            },
            '100%': {
                opacity: 1,
            },
        },
        backButton: {
            marginLeft: 30,
        },
        drawerMeetingsForm: {
            padding: '0 30px 80px 30px',
        },
        saveFooter: {
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            width: '100%',
            backgroundColor: theme.customization.background.C10,
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'end',
            alignItems: 'center',
            paddingRight: 32,
            paddingTop: 16,
            paddingBottom: 16,
        },
        formButton: {
            height: 40,
        },
        cancelButton: {
            border: 0,
            marginRight: 16,
        },
    }),
    { classNamePrefix: 'drawer-content-meetings' }
);
